import { useState, useEffect, useContext } from "react";
import { SvgEdgeCamera, SvgForward, SvgCog6ToothEmpty } from "ui/svgs";
import moment from "moment";
import { useParams } from "react-router-dom";
import clsx from "clsx";
import { Tooltip } from "antd";
import useWsSender from "lib/wsSenderHook";
import { SignalContext } from "context/SignalContext";
import { SideviewContext } from "context/SideviewContext";
import { CameraInfo } from "lib/types";
import { showToast } from "ui/components/common";
import { getCameraRule } from "pages/service";
import { AutomationContext } from "context/AutomationContext";
import Cookies from "js-cookie";
import { StreamingCameraType } from "lib/types";
import { personKey, vehicleKey, onMotorVehicleKey } from "lib/constants";
import { AuthContext } from "context/AuthContext";
import AutomationModal from "ui/automation/automationModal";
import LiveStreamModal from "ui/nvrs/live-stream-modal";
import { SvgLiveStreamEmpty, SvgDealerAutomation } from "ui/svgs";

export default function EdgeCameras({ edgeCameras }: { edgeCameras: any[] }) {
  const { orgid } = useParams();
  const { sendWsRequest } = useWsSender();
  const { user } = useContext(AuthContext);
  const { setOpenPanel, setViewData } = useContext(SideviewContext);
  const [wsRequestIds, setWsRequestIds] = useState(new Set());
  const [showLiveStream, setShowLiveStream] = useState(false);
  const [liveCamera, setLiveCamera] = useState(null);
  const [automationModalVisible, setAutomationModalVisible] = useState(false);
  const { sessionId } = useContext(SignalContext);
  const { setStreamingCamera, setResetedData, setSupportedEventTypes } =
    useContext(AutomationContext);

  async function handleClickCameraAutomation(objCamera) {
    const res = await getCameraRule(orgid, objCamera?.id, true);
    if (res.status !== 200) {
      showToast(1, "Failed to get camera rule. Please try again");
      return;
    }
    const cameraRule = res.data?.data;
    //const dataCredentials = await getCredentials([objCamera.nvrId]);
    const token = Cookies.get("token");

    const camera: StreamingCameraType = {
      devId: cameraRule.deviceId.toString(),
      type: cameraRule.type,
      orgId: orgid,
      nvrId: cameraRule.nvrId.toString(),
      cameraId: cameraRule.cameraId.toString(),
      cameraName: cameraRule.name,
      token: token!,
      turn: {},
      rtcToken: "",
      sessionId: sessionId,
      cameraMac: cameraRule.macAddress,
      remoteIndex: cameraRule.remoteIndex.toString(),
      streamId: "0",
      status: cameraRule.status,
      siteId: objCamera?.siteId.toString(),
      capabilities: cameraRule.capabilities,
    };
    // Sort detections by index
    //objCamera.detections.sort((a, b) => a.index - b.index);
    cameraRule?.algorithms?.forEach((alg) => {
      alg.detections.sort((a, b) => a.index - b.index);
    });

    const customOrder = [personKey, vehicleKey, onMotorVehicleKey];
    cameraRule.supportedEventTypes.sort(
      (a, b) => customOrder.indexOf(a) - customOrder.indexOf(b),
    );
    if (
      cameraRule.actions.length === 0 ||
      !cameraRule.actions[0]?.notifiers?.ownerNotifier?.types
    ) {
      cameraRule.actions = [
        {
          notifiers: {
            ownerNotifier: {
              types: [],
            },
          },
        },
      ];
    }
    setStreamingCamera(camera);
    setSupportedEventTypes(cameraRule.supportedEventTypes);
    setResetedData({
      alertSchedules: cameraRule.schedules,
      algorithms: cameraRule.algorithms,
      actions: cameraRule.actions,
      note: cameraRule.note,
      advancedAISetting: {
        enableAgi: cameraRule?.aiSettings?.enableAgi || false,
        enableCase: cameraRule?.aiSettings?.enableCase || false,
        enableNonMotor: cameraRule?.aiSettings?.enableNonMotor || false,
        enablePerson: cameraRule?.aiSettings?.enablePerson || false,
        enableVehicle: cameraRule?.aiSettings?.enableVehicle || false,
        virtualGuard: {
          cameraSpeakerEnabled:
            cameraRule?.aiSettings?.virtualGuard?.cameraSpeakerEnabled || false,
          ipSpeakerEnabled:
            cameraRule?.aiSettings?.virtualGuard?.ipSpeakerEnabled || false,
          speakerIds: cameraRule?.aiSettings?.virtualGuard?.speakerIds ?? [],
          pitch: cameraRule?.aiSettings?.virtualGuard?.pitch || "Medium",
          volume: cameraRule?.aiSettings?.virtualGuard?.volume || "Medium",
          rate: cameraRule?.aiSettings?.virtualGuard?.rate || "Medium",
        },
        aiAlgoSettings: cameraRule?.aiSettings?.aiAlgoSettings,
      },
    });
    setAutomationModalVisible(true);
  }

  const deviceForward = async (deviceType: string, deviceId: number) => {
    //start loading
    setWsRequestIds((state) => {
      state.add(deviceId);
      return new Set(state);
    });
    try {
      const res: any = await sendWsRequest(
        "device.remote_service.open",
        {
          deviceOpenRemoteService: {
            deviceId: deviceId.toString(),
            deviceType,
          },
        },
        { orgId: Number(orgid) },
      );
      const query =
        "?expiredAt=" +
        res.deviceOpenRemoteServiceReply.cookie.expiredAt +
        "&name=" +
        res.deviceOpenRemoteServiceReply.cookie.name +
        "&value=" +
        res.deviceOpenRemoteServiceReply.cookie.value;
      window.open(
        "https://" + res.deviceOpenRemoteServiceReply.domain + query,
        "_blank",
      );
    } catch (e) {
      showToast(1, "There was a problem forwarding nvr. Error:" + e);
    } finally {
      //end loading
      setWsRequestIds((state) => {
        state.delete(deviceId);
        return new Set(state);
      });
    }
  };

  return (
    <>
      {edgeCameras?.map((objData) => (
        <div
          key={objData?.id}
          className="py-3.5 w-full bg-zinc-50 flex justify-between group text-left items-center"
        >
          <div className="flex items-center w-3/12 min-w-[25%] 2xl:w-4/12 2x:min-w-[33.33%]  pr-3 justify-between">
            <span className="font-semibold mr-4 break-all">
              <SvgEdgeCamera
                width="22"
                className="fill-neutral-500 inline-block mr-2"
              />
              {objData.name}
            </span>
          </div>
          <div className="w-[16.67%] 2xl:w-1/12 min-w-[16.67%] 2xl:min-w-[8.33%] pr-[41px]">
            <div className="rounded-2xl text-base text-center2">
              <span
                className={clsx(
                  "inline-block w-3 h-3 rounded-full mr-1",
                  objData.status == "online" ? "bg-emerald-400" : "bg-red-500",
                )}
              ></span>
            </div>
          </div>
          <div className="w-3/12 min-w-[25%] pr-1">{objData.model}</div>
          <div className="w-1/12 min-w-[8.33%] pr-1">
            {objData.manufacturer}
          </div>
          <div className="w-[12.5%] min-w-[12.5%]">
            {objData.expiredAt
              ? "Exp " +
                moment(parseInt(objData.expiredAt)).format("YYYY/MM/DD")
              : "-"}
          </div>
          <div className="w-[12.5%]">
            <div className=" flex items-center justify-end flex-wrap">
              <Tooltip title="Automation">
                <button
                  className="hover:bg-neutral-200 rounded-full text-neutral-500 p-1 fill-[#717171]"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleClickCameraAutomation(objData);
                  }}
                >
                  <SvgDealerAutomation width="24" />
                </button>
              </Tooltip>
              <Tooltip title="Camera Forward">
                <button
                  className="hover:bg-neutral-200 rounded-full text-neutral-500 p-2 fill-[#717171]"
                  disabled={wsRequestIds?.has(objData.id)}
                  onClick={(event) => {
                    event.preventDefault();
                    deviceForward("edge_camera", objData.id);
                  }}
                >
                  {wsRequestIds?.has(objData.id) ? (
                    <div
                      className="ml-1 text-neutral-800  inline-block h-4 w-4 animate-spin rounded-full border-2 border-solid border-current border-r-transparent align-[-0.125em] motion-reduce:animate-[spin_1.5s_linear_infinite]"
                      role="status"
                    ></div>
                  ) : (
                    <>
                      <SvgForward width="16" />
                    </>
                  )}
                </button>
              </Tooltip>
              {user?.organization?.enableMonitoring && (
                <Tooltip title="Monitor">
                  <button
                    className="hover:bg-neutral-200 rounded-full text-neutral-500 p-1 fill-[#717171]"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();

                      setLiveCamera(objData);
                      setShowLiveStream(true);
                    }}
                  >
                    <SvgLiveStreamEmpty />
                  </button>
                </Tooltip>
              )}
              <Tooltip title="Edge Camera Settings">
                <button
                  className="hover:bg-neutral-200 rounded-full text-neutral-500 p-1 fill-neutral-800"
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const sideviewData = {
                      type: "edit-edge-camera",
                      title: "Camera Settings",
                      orgId: orgid,
                      siteId: objData?.siteId,
                      cameraId: objData?.id,
                      objData: objData,
                    };
                    setViewData(sideviewData);
                    setOpenPanel(true);
                  }}
                >
                  <SvgCog6ToothEmpty width="22" />
                </button>
              </Tooltip>
            </div>
          </div>
        </div>
      ))}

      <AutomationModal
        visible={automationModalVisible}
        onClose={() => setAutomationModalVisible(false)}
      />
      <LiveStreamModal
        visible={showLiveStream}
        camera={liveCamera}
        onClose={() => setShowLiveStream(false)}
      />
    </>
  );
}
